import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarRange, RotateCcw } from "lucide-react";

/**
 * Shared date-range picker used across the Admin, Restaurant-Partner and Rider
 * dashboards. Emits `{ start, end }` (ISO YYYY-MM-DD, both inclusive; either
 * can be empty) so the parent can pass them straight to the backend as query
 * params. Empty range = "all time".
 *
 * Presets are optional and rendered as pill buttons. Everything is controlled.
 */
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const startOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

const DateRangeFilter = ({
  start = "",
  end = "",
  onChange,
  compact = false,
  label = "Periodo",
  testIdPrefix = "date-range",
}) => {
  const presets = useMemo(() => [
    { key: "today", label: "Oggi", start: todayISO(), end: todayISO() },
    { key: "7d", label: "Ultimi 7g", start: daysAgo(6), end: todayISO() },
    { key: "30d", label: "Ultimi 30g", start: daysAgo(29), end: todayISO() },
    { key: "month", label: "Questo mese", start: startOfMonth(), end: todayISO() },
  ], []);

  const activePreset = presets.find(p => p.start === start && p.end === end)?.key;
  const isAllTime = !start && !end;

  return (
    <div
      className={`bg-white border border-navy/10 rounded-2xl ${compact ? "p-2" : "p-3"} flex items-center gap-2 flex-wrap`}
      data-testid={`${testIdPrefix}-root`}
    >
      <div className="flex items-center gap-1.5 text-navy/60 text-xs uppercase tracking-wider font-bold pr-1">
        <CalendarRange className="w-4 h-4" /> {label}
      </div>
      <Input
        type="date"
        value={start}
        max={end || undefined}
        onChange={(e) => onChange({ start: e.target.value, end })}
        className="h-9 w-[140px] font-mono text-sm"
        data-testid={`${testIdPrefix}-start`}
      />
      <span className="text-navy/40 text-xs">→</span>
      <Input
        type="date"
        value={end}
        min={start || undefined}
        onChange={(e) => onChange({ start, end: e.target.value })}
        className="h-9 w-[140px] font-mono text-sm"
        data-testid={`${testIdPrefix}-end`}
      />

      <div className="flex gap-1 flex-wrap">
        {presets.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange({ start: p.start, end: p.end })}
            className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border transition ${
              activePreset === p.key
                ? "bg-navy text-white border-navy"
                : "bg-white text-navy/70 border-navy/15 hover:border-coral hover:text-coral"
            }`}
            data-testid={`${testIdPrefix}-preset-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {!isAllTime && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange({ start: "", end: "" })}
          className="h-8 text-xs text-navy/60 hover:text-coral"
          data-testid={`${testIdPrefix}-reset`}
        >
          <RotateCcw className="w-3 h-3 mr-1" /> Tutti i tempi
        </Button>
      )}
    </div>
  );
};

export default DateRangeFilter;
